import React, { useRef, useState } from 'react';
import { AritoFormV2, BottomActionBarV2, DialogV2, HeaderTabsV2, type SearchFieldRef } from '@/components/custom/erp';
import { AritoIcon } from '@/components/custom/erp';

import type { TaiKhoan } from '@/types/schemas';
import { BasicInfo, DetailsTab } from './tabs';
import { searchSchema } from '../schema';
import { toast } from '@/hooks';

interface InitialSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSearch: (values: BanHangPayload) => void;
  searchFieldStates: BanHangRecord;
  searchParams: BanHangSearchParams;
}

export const InitialSearchDialog: React.FC<InitialSearchDialogProps> = ({
  open,
  onClose,
  onSearch,
  searchFieldStates,
  searchParams
}) => {
  const accountRef = useRef<SearchFieldRef<TaiKhoan>>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (data: BanHangPayload) => {
    setIsLoading(true);

    try {
      const result = await accountRef.current?.resolveCurrentInput();
      const accounts = result?.items ?? searchFieldStates.account;
      const error = result?.notFoundCodes ?? [];
      if (error.length > 0) {
        throw new Error(`Không tìm thấy tài khoản: ${error.join(', ')}`);
      }

      onSearch({
        ngay_ct1: data.ngay_ct1,
        ngay_ct2: data.ngay_ct2,
        kieu_xem: data.kieu_xem,
        group_by: data.group_by,
        mau_bc: data.mau_bc,
        tk: accounts.map((item: TaiKhoan) => item.uuid),
        ma_kh: searchFieldStates.customer?.uuid,
        nh_kh1: searchFieldStates.customerGroup1?.uuid,
        nh_kh2: searchFieldStates.customerGroup2?.uuid,
        nh_kh3: searchFieldStates.customerGroup3?.uuid,
        ma_khu_vuc: searchFieldStates.region?.uuid
      });
    } catch (error) {
      toast({
        description: error instanceof Error ? error.message : 'Có lỗi xảy ra vui lòng kiểm tra lại',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <DialogV2
      open={open}
      onClose={onClose}
      title='Bảng cân đối phát sinh công nợ'
      maxWidth='lg'
      disableBackdropClose={false}
      disableEscapeKeyDown={false}
      titleIcon={<AritoIcon icon={12} />}
    >
      <AritoFormV2
        mode='add'
        hasAritoActionBar={false}
        schema={searchSchema}
        initialData={searchParams}
        onSubmit={handleSubmit}
        className='w-full'
        headerFields={
          <div className='max-h-[calc(100vh-150px)] w-[1000px] overflow-y-auto'>
            <BasicInfo
              searchFieldStates={{
                account: searchFieldStates.account,
                setAccount: searchFieldStates.setAccount
              }}
              accountRef={accountRef}
            />

            <HeaderTabsV2
              tabs={[
                {
                  id: 'details',
                  label: 'Chi tiết',
                  component: (
                    <DetailsTab
                      searchFieldStates={{
                        customer: searchFieldStates.customer,
                        setCustomer: searchFieldStates.setCustomer,
                        customerGroup1: searchFieldStates.customerGroup1,
                        setCustomerGroup1: searchFieldStates.setCustomerGroup1,
                        customerGroup2: searchFieldStates.customerGroup2,
                        setCustomerGroup2: searchFieldStates.setCustomerGroup2,
                        customerGroup3: searchFieldStates.customerGroup3,
                        setCustomerGroup3: searchFieldStates.setCustomerGroup3,
                        region: searchFieldStates.region,
                        setRegion: searchFieldStates.setRegion
                      }}
                    />
                  )
                }
              ]}
            />
          </div>
        }
        bottomBar={<BottomActionBarV2 mode='search' onClose={onClose} />}
      />
    </DialogV2>
  );
};

export default InitialSearchDialog;
